/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { Badge, Button } from "@chakra-ui/react";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridClasses,
} from "@mui/x-data-grid";
import { Tooltip, IconButton } from "@mui/material";
import { MdOutlineContentCopy, MdOutlineCheckCircle } from "react-icons/md";
import { Modal, ModalOverlay, ModalContent, ModalBody } from "@chakra-ui/modal";
import axios from "axios";

const rowPerPage = 10;

type ReceiverAccountData = {
  id: string;
  created_at: string;
  asset: string;
  method_name: string;
  method_details: Record<string, any>;
  confirm_status: string;
};

interface PayoutReceiverAccountListModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (accountData: ReceiverAccountData) => void;
  receiverId: string;
}

interface PayloadType {
  [key: string]: any;
}

const PayoutReceiverAccountListModal: React.FC<
  PayoutReceiverAccountListModalProps
> = ({ isOpen, onClose, onSelect, receiverId }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tableData, setTableData] = useState<ReceiverAccountData[]>([]);

  // Pagination state following receiver/index.tsx pattern
  const [lastKeys, setLastKeys] = useState<{ [key: number]: string }>({});
  const [currentLastKey, setCurrentLastKey] = useState<string>("");
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(rowPerPage);
  const [rowCount, setRowCount] = useState<number>(rowPerPage);

  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  // Fetch when modal opens
  useEffect(() => {
    if (isOpen && receiverId) {
      setTableData([]);
      setPage(0);
      setLastKeys({});
      setCurrentLastKey("");
      setRowCount(rowPerPage);
      fetchReceiverAccounts({
        page: 0,
        limit: pageSize,
        lastkey: "",
        receiver_id: receiverId,
      });
    }
  }, [isOpen, receiverId]);

  const fetchReceiverAccounts = async (payload: PayloadType = {}) => {
    try {
      setIsLoading(true);

      const queryObject: PayloadType = {
        limit: payload?.limit || pageSize,
        lastkey: payload?.lastkey || "",
        receiver_id: payload?.receiver_id || receiverId,
        usertype: userRole === "superadmin" ? "a" : "m",
      };

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/receiveraccount/get-all?${queryString}`
      );

      const newData: ReceiverAccountData[] =
        response.data.receiver_accounts?.payload || [];

      if (newData.length !== 0) {
        setTableData(newData);
      } else {
        setTableData([]);
      }

      // Manage Last Key and Total Record (following receiver/index.tsx pattern)
      const newLastKey = response.data.receivers?.lastkey;
      if (newLastKey) {
        setCurrentLastKey(newLastKey);
        const newPage = payload?.page || 0;
        const newPageSize = payload?.limit || pageSize;

        if (newData.length !== 0) {
          setRowCount((newPage + 1) * newPageSize + newPageSize);
        } else {
          setRowCount(newPage * newPageSize + newPageSize);
        }
        setLastKeys((prev) => ({ ...prev, [newPage]: newLastKey }));
      } else {
        // No more pages
        const newPage = payload?.page || 0;
        const newPageSize = payload?.limit || pageSize;
        setRowCount(newPage * newPageSize + newData.length);
        setCurrentLastKey("");
      }
    } catch (error) {
      console.error("Error fetching receiver accounts", error);
      setTableData([]);
      // Clear all
      setPage(0);
      setRowCount(0);
      setLastKeys({});
      setCurrentLastKey("");
    } finally {
      setIsLoading(false);
    }
  };

  const setPaginationModel = (newValue: any) => {
    setIsLoading(true);

    let newFilters: PayloadType = {
      page: newValue.page,
      limit: newValue.pageSize,
      lastkey: currentLastKey,
      receiver_id: receiverId,
    };

    // If click prev pagination
    if (newValue.page < page) {
      newFilters = {
        ...newFilters,
        lastkey: newValue.page !== 0 ? lastKeys[newValue.page - 1] : "",
      };
    }

    // if select new row per page, reset page to 0, reset lastkey
    if (newValue.pageSize !== pageSize) {
      newFilters = {
        page: 0,
        limit: newValue.pageSize,
        lastkey: "",
        receiver_id: receiverId,
      };
      setPage(0);
      setPageSize(newValue.pageSize);
      setCurrentLastKey("");
      setLastKeys({});
    } else {
      setPage(newValue.page);
    }

    fetchReceiverAccounts(newFilters);
  };

  const handleSelect = (account: ReceiverAccountData) => {
    onSelect(account);
    onClose();
  };

  const handleClickCopy = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
    } catch (err) {
      console.error("Failed to copy ID: ", err);
    }
  };

  const formatMethodDetails = (methodDetails: Record<string, any>) => {
    return Object.entries(methodDetails)
      .filter(([, value]) => value !== "")
      .map(([key, value]) => `${formatLabel(key)}: ${value}`)
      .join(" / ");
  };

  const formatLabel = (label: string) => {
    let modifiedLabel = label.split("_").join(" ");
    switch (label) {
      case "bsb":
        modifiedLabel = "BSB";
        break;
      case "bic":
        modifiedLabel = "BIC";
        break;
      case "iban":
        modifiedLabel = "IBAN";
        break;
    }
    return modifiedLabel;
  };

  const statusClasses = "status";

  const statusList = [
    {
      status: "unconfirmed",
      display: (
        <Badge className={`${statusClasses} status-unconfirmed`}>
          Unconfirmed
        </Badge>
      ),
    },
    {
      status: "sent_mail",
      display: (
        <Badge className={`${statusClasses} status-sent_mail`}>
          Email Sent
        </Badge>
      ),
    },
    {
      status: "confirmed",
      display: (
        <Badge className={`${statusClasses} status-confirmed`}>Confirmed</Badge>
      ),
    },
  ];

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "",
      flex: 1,
      minWidth: 50,
      maxWidth: 50,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (
        params: GridRenderCellParams<ReceiverAccountData, string>
      ) => (
        <Tooltip title="Copy Account ID" arrow placement="top">
          <div>
            <IconButton
              id={"button-copy-accountid-" + params?.row?.id}
              onClick={() => handleClickCopy(params.row.id)}
            >
              <MdOutlineContentCopy className="h-5 w-5 text-navy-800" />
            </IconButton>
          </div>
        </Tooltip>
      ),
    },
    {
      field: "asset",
      headerName: "Asset",
      minWidth: 80,
      flex: 1,
      sortable: false,
    },
    {
      field: "method_name",
      headerName: "Method",
      flex: 1,
      minWidth: 120,
      sortable: false,
    },
    {
      field: "method_details",
      headerName: "Account Details",
      flex: 2,
      minWidth: 300,
      sortable: false,
      renderCell: (
        params: GridRenderCellParams<ReceiverAccountData, Record<string, any>>
      ) => {
        return formatMethodDetails(params.value || {});
      },
    },
    {
      field: "confirm_status",
      headerName: "Status",
      flex: 1,
      minWidth: 120,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (
        params: GridRenderCellParams<ReceiverAccountData, string>
      ) => {
        const status = params.value as string;
        const statusItem = statusList.find((item) => item.status === status);
        return statusItem ? statusItem.display : null;
      },
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      flex: 1,
      minWidth: 100,
      headerAlign: "right",
      align: "right",
      renderCell: (params) => {
        return (
          <Tooltip title="Select Account" arrow placement="top">
            <IconButton
              id={"button-select-" + params?.row?.id}
              onClick={() => handleSelect(params.row)}
            >
              <MdOutlineCheckCircle className="h-5 w-5" />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  const customDataGridStyles: Partial<GridClasses> = {
    root: "mui-table-custom",
    columnHeader: "text-start",
    row: "",
  };

  return (
    <>
      {isOpen && (
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          closeOnOverlayClick={false}
          size="6xl"
        >
          <ModalOverlay className="bg-[#000] !opacity-30" />
          <ModalContent className="dark:!bg-neutral-900 !z-[9999] !m-auto !w-full !max-w-[90%] !rounded-2xl !border !border-gray-200 !bg-white !shadow-xl dark:!border-white/10 md:!top-[3vh] lg:!max-w-[1100px]">
            <ModalBody className="p-0">
              <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-4 dark:border-white/10 md:px-7">
                <div className="flex items-start gap-3">
                  <div className="mt-1 rounded-xl bg-primary-50 p-2 text-primary-600 ring-1 ring-primary-100 dark:bg-primary-900/20 dark:text-primary-300 dark:ring-primary-900/30">
                    <svg
                      viewBox="0 0 24 24"
                      className="h-6 w-6"
                      fill="currentColor"
                    >
                      <path d="M4 10v7h3v-7H4zm6 0v7h3v-7h-3zM2 22h19v-3H2v3zm14-12v7h3v-7h-3zm-4.5-9L2 6v2h19V6l-9.5-5z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Select Payment Account
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Choose a receiver account for your payout
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-3xl border border-gray-200 text-gray-600 transition hover:bg-gray-50 hover:text-gray-700 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5"
                  aria-label="Close"
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M18 6 6 18" />
                    <path d="M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Body (scroll) */}
              <div className="max-h-[70vh] overflow-y-auto">
                {/* Table */}
                <div className="px-6 py-5 md:px-7">
                  <Box className="inner-border w-full overflow-hidden rounded-b-xl rounded-t-lg bg-white">
                    <DataGrid
                      sx={{
                        "& .MuiDataGrid-cell": { padding: "8px 16px" },
                        "& .MuiDataGrid-columnHeader": { padding: "8px 16px" },
                        "& .MuiTablePagination-input": { marginRight: "-20px" },
                        "& .MuiTablePagination-displayedRows": {
                          display: "none",
                        },
                        border: "none",
                      }}
                      loading={isLoading}
                      rows={tableData || []}
                      columns={columns}
                      getRowId={(row) => row.id}
                      rowCount={rowCount}
                      sortingMode="server"
                      paginationMode="server"
                      pageSizeOptions={[10]}
                      rowHeight={48}
                      columnHeaderHeight={48}
                      paginationModel={{ page: page, pageSize: pageSize }}
                      onPaginationModelChange={setPaginationModel}
                      disableRowSelectionOnClick
                      disableColumnFilter
                      disableColumnMenu
                      classes={customDataGridStyles}
                      hideFooter={currentLastKey === "" && page === 0}
                      autoHeight
                      localeText={{
                        noRowsLabel: "No receiver accounts found",
                      }}
                    />
                  </Box>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-6 py-4 dark:border-white/10 md:px-7">
                <Button
                  type="button"
                  onClick={onClose}
                  className="dark:bg-neutral-900 h-10 rounded-lg border border-gray-200 bg-white px-5 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
                >
                  Close
                </Button>
              </div>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default PayoutReceiverAccountListModal;
